/* =====================================================================
   08 Customer reviews — hydration for ns--section-homepage-s8.liquid

   The section is already readable when this runs: Liquid server-rendered the aggregate, the
   photo strip and the newest cards out of the `reviews_stats` metaobject. This script takes
   over filtering, sorting, pagination and the photo gallery.

   Loading, in order of cost:
     index-1  (7KB, newest 100)  — fetched immediately, replaces the server-rendered cards.
     index-2  (238KB, the rest)  — fetched ONLY when it is actually needed: the visitor
                                   filters/sorts, opens the gallery, or the section is
                                   uncapped (the /pages/reviews archive).
     photos   (5KB, all 246)     — fetched when the gallery opens, so the grid paints without
                                   waiting on index-2.

   Filtering is client-side by necessity, not preference: the Storefront API's `metaobjects`
   query takes no filter argument and sorts only by id/updated_at, so a query like
   "5 stars + tag, newest first" cannot be asked of the store.

   A review's position in the combined [index-1, index-2] array is its identity — that is the
   `k` in reviews-photos.json. `pool` therefore must never be reordered in place.
   ===================================================================== */
(() => {
  const INIT = 6;
  const STEP = 6;
  const LONG_BODY = 320; // chars past which a card clamps and offers "Read more"

  const ARROW = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M6 13l6 6 6-6"/></svg>';
  const ARROW_R = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';

  const ENT = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  // Review text is visitor-written. Even the benign stuff bites: bodies contain "<3".
  const esc = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, (c) => ENT[c]);
  const cdn = (u, w) => u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // One in-flight promise per URL, shared by every section on the page.
  const jsonCache = {};
  const getJSON = (url) => {
    if (!jsonCache[url]) {
      jsonCache[url] = fetch(url).then((r) => {
        if (!r.ok) throw new Error(url + ' -> ' + r.status);
        return r.json();
      });
    }
    return jsonCache[url];
  };

  const DAY = 86400000;
  // Mirrors the Liquid in the section — keep the two in step or cards shift on hydration.
  function ago(d) {
    const t = Date.parse(d + 'T00:00:00Z');
    if (!t) return '';
    const days = Math.floor((Date.now() - t) / DAY);
    if (days < 1) return 'today';
    if (days < 7) return days + ' days ago';
    if (days < 35) return Math.floor(days / 7) + 'w ago';
    if (days < 365) return Math.floor(days / 30) + 'mo ago';
    return Math.floor(days / 365) + 'y ago';
  }

  const stars = (n) => {
    let out = '';
    for (let i = 1; i <= 5; i++) out += '<i' + (i <= n ? '' : ' class="off"') + '>&#9733;</i>';
    return out;
  };

  function card(r) {
    const name = r.n || 'Anonymous';
    const initial = name.trim().charAt(0).toUpperCase() || '?';
    const body = r.b || '';
    const long = body.length > LONG_BODY;
    const shots = (r.im || [])
      .map((u) => '<button type="button" data-rv-shot="' + esc(u) + '" aria-label="Open photo">' +
        '<img src="' + esc(cdn(u, 120)) + '" alt="" loading="lazy" decoding="async"></button>')
      .join('');
    return '<article class="rv-card">' +
      '<div class="rv-id"><div class="rv-av" aria-hidden="true">' + esc(initial) + '</div><div>' +
        '<div class="nm">' + esc(name) + '</div>' +
        (r.v ? '<div class="vf">Verified purchase</div>' : '') +
      '</div></div>' +
      '<div class="rv-meta"><span class="st" role="img" aria-label="' + esc(r.r) + ' out of 5">' +
        stars(r.r) + '</span><span class="ago">' + esc(ago(r.d)) + '</span></div>' +
      (r.ti ? '<p class="rv-ttl">' + esc(r.ti) + '</p>' : '') +
      '<p class="rv-quote' + (long ? ' clamp' : '') + '">&ldquo;' + esc(body) + '&rdquo;</p>' +
      (long ? '<button class="rv-readmore" type="button" data-rv-readmore>Read more</button>' : '') +
      (shots ? '<div class="rv-shots">' + shots + '</div>' : '') +
      '<div class="rv-foot"><span class="rv-src">' + esc(r.src || '') + '</span></div>' +
    '</article>';
  }

  function init(root) {
    const grid = root.querySelector('[data-rv-grid]');
    if (!grid) return;
    const reveal = root.querySelector('[data-rv-reveal]');
    const moreBtn = root.querySelector('[data-rv-more]');
    const filtersEl = root.querySelector('[data-rv-filters]');
    const sortEl = root.querySelector('[data-rv-sort]');
    const dist = root.querySelector('[data-rv-dist]');

    const cap = parseInt(root.dataset.rvCap, 10) || 0; // 0 = the whole archive
    const total = parseInt(root.dataset.rvTotal, 10) || 0;
    const totalFmt = root.dataset.rvTotalFmt || String(total);
    const allUrl = root.dataset.rvAllUrl || '';
    const url1 = root.dataset.rvIndex1;
    const url2 = root.dataset.rvIndex2;
    const urlPh = root.dataset.rvPhotos;

    let pool = [];        // [index-1 rows, ...index-2 rows] — order IS identity, never sort it
    let deep = false;     // index-2 merged in?
    let open = false;     // has the grid been expanded past its first clamp?
    let shown = INIT;
    let cacheList = null;

    // The default view is whatever Liquid marked up — pressed chips and the selected option.
    // Reading it from the DOM keeps the default in one place and guarantees the server-rendered
    // cards match what this script paints first. Note a default filter is NOT an interaction:
    // index-2 stays unfetched until the visitor actually asks something of the archive.
    let sort = (sortEl && sortEl.value) || 'recent';
    const active = new Set();
    if (filtersEl) {
      filtersEl.querySelectorAll('.rv-chip[aria-pressed="true"]').forEach((c) => active.add(c.dataset.filter));
    }

    let photos = null;
    let lbAt = -1;

    const clampPx = () => Math.round(Math.max(380, Math.min(700, window.innerHeight * 0.56)));

    // One dialog per section, with its own state. A page-wide singleton would double-bind its
    // listeners and cross its `photos`/`lbAt` the moment a page carries two review sections.
    let lbEl = null;
    function lightbox() {
      if (lbEl) return lbEl;
      lbEl = document.createElement('dialog');
      lbEl.className = 'rv-lb';
      lbEl.innerHTML =
        '<div class="rv-lb-bar"><span data-lb-title></span>' +
        '<button class="rv-lb-x" type="button" aria-label="Close">&times;</button></div>' +
        '<div class="rv-lb-scroll"><div data-lb-body></div></div>';
      lbEl.querySelector('.rv-lb-x').addEventListener('click', () => lbEl.close());
      lbEl.addEventListener('click', (e) => { if (e.target === lbEl) lbEl.close(); });
      lbEl.addEventListener('click', (e) => {
        const one = e.target.closest('[data-lb-i]');
        if (one && photos) { lbAt = +one.dataset.lbI; renderLbOne(); return; }
        if (e.target.closest('[data-lb-all]')) { renderLbGrid(); return; }
        if (e.target.closest('[data-lb-prev]')) { if (lbAt > 0) { lbAt--; renderLbOne(); } return; }
        if (e.target.closest('[data-lb-next]')) { if (photos && lbAt < photos.length - 1) { lbAt++; renderLbOne(); } }
      });
      document.addEventListener('keydown', (e) => {
        if (!lbEl.open || lbAt < 0 || !photos) return;
        if (e.key === 'ArrowLeft' && lbAt > 0) { lbAt--; renderLbOne(); }
        if (e.key === 'ArrowRight' && lbAt < photos.length - 1) { lbAt++; renderLbOne(); }
      });
      document.body.appendChild(lbEl);
      return lbEl;
    }

    async function ensureDeep() {
      if (deep || !url2) return;
      root.setAttribute('data-rv-busy', '');
      try {
        pool = pool.concat(await getJSON(url2));
        deep = true;
        cacheList = null;
      } catch (e) {
        // Keep the section usable on the newest 100 rather than breaking outright.
        console.warn('[ns-reviews]', e);
      }
      root.removeAttribute('data-rv-busy');
    }

    function pass(r) {
      for (const f of active) {
        if (f === '__verified') { if (!r.v) return false; }
        else if (f === '__photos') { if (!r.im || !r.im.length) return false; }
        else if (!r.t || r.t.indexOf(f) === -1) return false;
      }
      return true;
    }

    function cmp(a, b) {
      if (sort === 'high') return (b.r - a.r) || b.d.localeCompare(a.d);
      if (sort === 'low') return (a.r - b.r) || b.d.localeCompare(a.d);
      return b.d.localeCompare(a.d);
    }

    // filter() copies, so pool's own order survives — see the header note about `k`.
    function list() {
      if (!cacheList) cacheList = pool.filter(pass).sort(cmp);
      return cacheList;
    }
    const capped = () => (cap ? list().slice(0, cap) : list());

    function setMore() {
      const c = capped();
      const remaining = c.length - shown;
      moreBtn.hidden = false;
      moreBtn.classList.remove('done');
      delete moreBtn.dataset.rvLink;

      if (!open) {
        moreBtn.innerHTML = '<span class="lbl">Show more reviews</span>' + ARROW;
      } else if (remaining > 0) {
        moreBtn.innerHTML = '<span class="lbl">Show more reviews</span> <span class="ct">+' +
          Math.min(STEP, remaining) + '</span>' + ARROW;
      } else if (cap && allUrl && total > c.length) {
        // This instance is a window onto the archive; hand the visitor over to the full page.
        moreBtn.dataset.rvLink = allUrl;
        moreBtn.innerHTML = '<span class="lbl">Read all ' + esc(totalFmt) + ' reviews</span>' + ARROW_R;
      } else {
        moreBtn.hidden = true; // everything there is to show is on screen
      }
    }

    // Nothing is hidden if the cards already fit inside the clamp — drop the fade and the
    // expander rather than veil empty space (a narrow filter leaves only a card or two).
    function settle() {
      if (open || grid.scrollHeight > clampPx() + 8) return;
      open = true;
      reveal.classList.add('open');
      grid.style.maxHeight = 'none';
      setMore();
    }

    function render(animateFrom) {
      const vis = capped().slice(0, shown);
      grid.innerHTML = vis.length
        ? vis.map(card).join('')
        : '<div class="rv-empty">No reviews match this filter.</div>';
      if (animateFrom != null && !reduced) {
        Array.prototype.forEach.call(grid.children, (c, i) => {
          if (i >= animateFrom) {
            c.classList.add('enter');
            c.style.animationDelay = ((i - animateFrom) * 65) + 'ms';
          }
        });
      }
      setMore();
      settle();
    }

    function grow(mutate) {
      const start = grid.getBoundingClientRect().height;
      grid.style.maxHeight = start + 'px';
      void grid.offsetHeight;
      if (mutate) mutate();
      grid.style.maxHeight = grid.scrollHeight + 'px';
      let done = false;
      const fin = () => {
        if (done) return;
        done = true;
        grid.style.maxHeight = 'none';
        grid.removeEventListener('transitionend', onEnd);
      };
      const onEnd = (e) => { if (e.propertyName === 'max-height') fin(); };
      grid.addEventListener('transitionend', onEnd);
      setTimeout(fin, 1100);
    }

    function collapse() {
      open = false;
      reveal.classList.remove('open');
      grid.style.maxHeight = clampPx() + 'px';
    }

    async function refilter() {
      shown = INIT;
      cacheList = null;
      collapse();
      render();
      await ensureDeep(); // honest counts need the archive, not just the newest 100
      cacheList = null;
      render();
    }

    // ---- gallery ------------------------------------------------------
    function lbTitle(t) { lightbox().querySelector('[data-lb-title]').textContent = t; }
    const lbBody = () => lightbox().querySelector('[data-lb-body]');

    function renderLbGrid() {
      lbAt = -1;
      lbTitle(photos.length + ' customer photos');
      lbBody().innerHTML = '<div class="rv-lb-grid">' + photos.map((p, i) =>
        '<button type="button" data-lb-i="' + i + '" aria-label="Open photo ' + (i + 1) + '">' +
        '<img src="' + esc(cdn(p.u, 240)) + '" alt="" loading="lazy" decoding="async"></button>'
      ).join('') + '</div>';
    }

    function renderLbOne() {
      const p = photos[lbAt];
      const r = pool[p.k]; // undefined until index-2 lands — caption fills in then
      lbTitle((lbAt + 1) + ' / ' + photos.length);
      lbBody().innerHTML = '<div class="rv-lb-one">' +
        '<img src="' + esc(cdn(p.u, 1400)) + '" alt="Customer photo">' +
        (r ? '<p class="rv-lb-cap"><span class="who">' + esc(r.n || 'Anonymous') + '</span> · ' +
              esc(r.r) + '/5 · ' + esc(ago(r.d)) + '<br>&ldquo;' + esc(r.b || '') + '&rdquo;</p>' : '') +
        '<div class="rv-lb-nav">' +
          '<button type="button" data-lb-prev aria-label="Previous"' + (lbAt <= 0 ? ' disabled' : '') + '>&lsaquo;</button>' +
          '<button type="button" data-lb-all aria-label="All photos">&#9638;</button>' +
          '<button type="button" data-lb-next aria-label="Next"' + (lbAt >= photos.length - 1 ? ' disabled' : '') + '>&rsaquo;</button>' +
        '</div></div>';
    }

    async function openGallery(startK, startUrl) {
      const el = lightbox();
      if (!el.open) el.showModal();
      lbTitle('Loading photos…');
      lbBody().innerHTML = '';
      if (!photos) {
        try { photos = await getJSON(urlPh); }
        catch (e) { lbTitle('Photos unavailable'); return; }
      }
      // Captions need the review behind each photo; don't make the grid wait for it.
      ensureDeep().then(() => { if (lbEl.open && lbAt >= 0) renderLbOne(); });
      let at = -1;
      if (startUrl != null) at = photos.findIndex((p) => p.u === startUrl);
      else if (startK != null) at = photos.findIndex((p) => p.k === startK);
      if (at >= 0) { lbAt = at; renderLbOne(); } else renderLbGrid();
    }

    // ---- events -------------------------------------------------------
    moreBtn.addEventListener('click', () => {
      if (moreBtn.dataset.rvLink) { window.location.href = moreBtn.dataset.rvLink; return; }
      if (!open) {
        open = true;
        reveal.classList.add('open');
        grow();
        setMore();
        return;
      }
      const prev = grid.querySelectorAll('.rv-card').length;
      grow(() => { shown += STEP; render(prev); });
    });

    if (filtersEl) {
      filtersEl.addEventListener('click', (e) => {
        const chip = e.target.closest('.rv-chip');
        if (!chip) return;
        const f = chip.dataset.filter;
        if (active.has(f)) { active.delete(f); chip.setAttribute('aria-pressed', 'false'); }
        else { active.add(f); chip.setAttribute('aria-pressed', 'true'); }
        refilter();
      });
    }

    if (sortEl) {
      sortEl.addEventListener('change', () => { sort = sortEl.value; refilter(); });
    }

    grid.addEventListener('click', (e) => {
      const more = e.target.closest('[data-rv-readmore]');
      if (more) {
        const q = more.previousElementSibling;
        if (q) q.classList.remove('clamp');
        more.remove();
        return;
      }
      const shot = e.target.closest('[data-rv-shot]');
      if (shot) openGallery(null, shot.dataset.rvShot);
    });

    const galleryBtn = root.querySelector('[data-rv-gallery]');
    if (galleryBtn) galleryBtn.addEventListener('click', () => openGallery());
    root.querySelectorAll('[data-rv-thumb]').forEach((t) => {
      t.addEventListener('click', () => openGallery(+t.dataset.rvThumb));
    });

    window.addEventListener('resize', () => { if (!open) grid.style.maxHeight = clampPx() + 'px'; });

    // ---- histogram: correct without JS, animated with it ---------------
    if (dist && !reduced) {
      const bars = dist.querySelectorAll('.bar i');
      bars.forEach((b) => { b.style.width = '0'; });
      const fill = () => bars.forEach((b) => { b.style.width = ''; });
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((en) => {
          en.forEach((x) => { if (x.isIntersecting) { fill(); io.disconnect(); } });
        }, { threshold: 0.3 });
        io.observe(dist);
      } else fill();
      setTimeout(fill, 1500);
    }

    // ---- go -----------------------------------------------------------
    collapse();
    getJSON(url1)
      .then((rows) => { pool = rows; cacheList = null; render(); })
      .catch((e) => console.warn('[ns-reviews]', e)); // server-rendered cards stay put

    // The archive page is the one place index-2 is the whole point.
    if (!cap) {
      const idle = window.requestIdleCallback || ((f) => setTimeout(f, 400));
      idle(() => ensureDeep().then(() => render()));
    }
  }

  const boot = () => document.querySelectorAll('[data-rv]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
